import React, { useEffect } from 'react';
import { Route, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import { useStore } from "../useStore";
import { cartSetInitial, cartEmpty, cartReset } from '../reducers/cart';
import { quickshopStyles } from './styles';
import NavBar from './NavBar';
import AppBottomNavigation from './AppBottomNavigation';
import Crud from './Crud';
import ProductList from './ProductList';
import CartList from './CartList';
import OrderList from './OrderList';
import { ReadMore } from './Utils';
import { CustomerContext, BreweryContext, CartContext } from './Context';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';

const BreweryInfo = ({ brewery }) => (
  <Grid item align="center">
    {brewery && brewery.info && brewery.info.split('\n').map((line, index) => <Typography key={index} variant="h6">{line}</Typography>)}
  </Grid>
)

const Products = withStyles(quickshopStyles)(({ brewery }) => (
  <>
    <Helmet>
      <title>Produkter - QuickShop</title>
      <meta name="description" content="Köp produkter från bryggerier." />
    </Helmet>
    <Crud resource={`product`}>
      {crud => <ProductList brewery={brewery} crud={crud} />}
    </Crud>
  </>
))

const Cart = withStyles(quickshopStyles)(({ classes, brewery, createOrder }) => (
  <>
    <Helmet>
      <title>Varukorg - QuickShop</title>
      <meta name="description" content="Köp produkter från bryggerier." />
    </Helmet>
    <CartList brewery={brewery} createOrder={createOrder} />
  </>
))

const Orders = withStyles(quickshopStyles)(({ brewery, classes }) => (
  <>
    <Helmet>
      <title>Beställningar - QuickShop</title>
      <meta name="description" content="Köp produkter från bryggerier." />
    </Helmet>
    <Crud resource={`order`}>
      {crud => <OrderList brewery={brewery} crud={crud} />}
    </Crud>
  </>
))

const Copyright = withStyles(quickshopStyles)(props => (
  <Grid container justify="center">
    <Grid item>
      <Typography className={props.classes.copyright}>(c) SwedBeer.se</Typography>
    </Grid>
  </Grid>
))

const Info = ({ brewery, customer }) => (
  <Typography align="center">
    Bryggeri: {brewery ? brewery.name : ''} - Kund: {customer ? customer.company_name : ''}
  </Typography>
)
const Brewery = ({ match, customer, crud }) => {
  const { state, dispatch } = useStore();
  const breweryId = parseInt(match.params.id);
  const brewery = crud.items ? crud.items.find(b => b.id === breweryId) : {};
  const breweryCartId = `cart_${breweryId}_${customer.id}`;
  if (!state.cartLoaded && brewery) {
    const cartString = sessionStorage.getItem(breweryCartId) || JSON.stringify({
      items: [],
      comment: '',
      yourReference: brewery.your_reference,
      deliveryInstructions: brewery.delivery_instructions,
      productSearch: '',
      purchaseOrderNumber: '',
    });
    const cart = JSON.parse(cartString);
    dispatch(cartSetInitial(cart));
  }

  useEffect(() => {
    let timerId = setInterval(() => sessionStorage.setItem(breweryCartId, JSON.stringify(state.cart)), 2000);
    return () => clearTimeout(timerId);
  }, [state]);

  if (brewery && brewery.id) {
    return (<>
      <BreweryContext.Provider value={brewery}>
        <CartContext.Provider value={state.cart}>
          <Route exact path={`${match.path}`} render={props => <Products brewery={brewery} />} />
          <Route exact path={`${match.path}cart`} render={props => <Cart brewery={brewery} createOrder={order => { crud.detailAction(brewery.id, 'order', order).then(json => { if (json.result === "success") { dispatch(cartEmpty(state.cart)); toast.success('Beställning skickad!'); } else { toast.error('Det uppstod ett fel!') } }) }} />} />
          <Route exact path={`${match.path}orders`} render={props => <Orders brewery={brewery} />} />
          <Copyright />
          <AppBottomNavigation brewery={brewery} match={match} />
        </CartContext.Provider>
      </BreweryContext.Provider>
    </>
    )
  }
  return <div>Loading...</div>
}

const BreweryList = withStyles(quickshopStyles)(({ items, classes, customer, customers, setCustomer }) => (<>
  <Helmet>
    <title>Bryggerier - QuickShop</title>
    <meta name="description" content="Välj bryggeri från listan och börja handla direkt." />
  </Helmet>
  <Grid container justify="center">
    <Grid item md={6}>
      <Typography className={classes.topText} align="center">Här kan ni beställa direkt från bryggeriernas egna distribution. Bryggerierna måste aktivera er som kund för att ni skall kunna lägga beställning. Vill ni sambeställa från flera bryggerier eller från centrallagret trycker ni på "Hem"-ikonen och använder "Handla drycker" istället.</Typography>
    </Grid>
  </Grid>
  <Grid container justify="center">
    <Grid item>
      <FormControl>
        <InputLabel>Byt kund</InputLabel>
        <Select value={(customer && customer.id) || 0} onChange={(event, menuItem) => setCustomer && setCustomer(event.target.value)}>
          {customers && customers.map(item => <MenuItem key={item.id} value={item.id}>{item.company_name}</MenuItem>)}
        </Select>
      </FormControl>
    </Grid>
  </Grid>

  <Grid container direction="column" alignItems="center">
    <Grid item xs={11} sm={10} md={8} lg={6} xl={4}>
      <Grid container direction="column" spacing={16} alignItems="stretch">
        {items && items.map(item => (
          <Grid item key={item.id}>
            <Card item={item}>
              <CardHeader title={item.name} />
              <CardContent>
                <Typography>
                  <ReadMore
                    lines={2}
                    readMoreText={<span>Läs mer</span>}>
                    <span dangerouslySetInnerHTML={{ __html: item.description }}></span>
                  </ReadMore>
                </Typography>
              </CardContent>
              <CardActions style={{ justifyContent: "flex-end" }}>
                <Button variant="contained" color="primary" component={Link} to={`${item.id}/`}>Handla</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Grid>
  </Grid>
</>))

const setCustomer = (customerId, customerCrud, breweryCrud, dispatch) => {
  return customerCrud.detailAction(customerId, 'set_current').then(data => { if (data.result === "success") { customerCrud.getItems({}, true); breweryCrud.getItems({}, true); dispatch(cartReset()); } })
}

const Breweries = ({ match, customer, breweryCrud, customerCrud }) => {
  const { dispatch } = useStore();
  useEffect(() => {
    breweryCrud.getItems({});
  }, []);
  return (
    <>
      <Route exact path={`${match.path}`} render={props => <BreweryList items={breweryCrud.items} customers={customerCrud.items} customer={customer} setCustomer={customerId => setCustomer(customerId, customerCrud, breweryCrud, dispatch)} {...props} />} />
      <Route strict path={`${match.path}:id/`} render={props => <Brewery customer={customer} crud={breweryCrud} {...props} />} />
    </>
  )
}

const Customers = ({ match, customerCrud }) => {
  useEffect(() => {
    customerCrud.getItems({});
  }, []);
  if (customerCrud.items) {
    if (customerCrud.items.length > 0) {
      const customer = customerCrud.items && (customerCrud.items.find(customer => customer.current) || customerCrud.items[0]);
      return (
        <CustomerContext.Provider value={customer}>
          <Crud resource={`brewery`}>
            {breweryCrud => <Breweries customer={customer} match={match} breweryCrud={breweryCrud} customerCrud={customerCrud} />}
          </Crud>
        </CustomerContext.Provider>
      )
    } else {
      return (
        <Typography align="center">Hittade inga kunder. <Button variant="outlined" href="/sv/customer/create/?next=/quickshop/">Skapa kund</Button></Typography>
      )
    }
  } else {
    return (
      <Typography align="center">
        Var god och logga in:
        <Button variant="outlined" href="/sv/accounts/login/?next=/quickshop/">Logga in</Button>
      </Typography>
    )
  }

}

const QuickShop = ({ match, history, location }) => {
  return (<>
    <NavBar />
    <Crud resource="customer">
      {customerCrud => <Customers match={match} customerCrud={customerCrud} />}
    </Crud>
  </>)
}
export default QuickShop;
